export namespace BwsLora {
  export interface AutoTest {
    DEV: string; //DM_BWS_LORA,
    SN: string; //FFFFFFFF,
    FW: string; //BWSIot_Lora_V_01.00.00,
    ACELCOM: string; //OK,
    ACELID: string; //23,
    ADMAIN: string; //122,
    ADBACK: string; //404,
    ADNTC: string; //24,
    IN1: string; //OK,
    IN2: string; //OK,
    OUT1: string; //OK,
    CHARGER: string; //OK,
    GPSCOM: string; //OK,
    GPSVCC: string; //OK,
    GPSRST: string; //OK
  }
}

export class BwsLoraParser {
  /**
   * Extrai o valor do serial de uma string que contém "RINS=" seguido de um número.
   *
   * @param input - A string que contém a informação do serial.
   * @returns O valor do serial ou undefined se o formato não for válido.
   */
  static serial(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RINS=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'timestamp' de uma string que contém "RTK=" seguido de um número.
   *
   * @param input - A string que contém a informação do 'timestamp'.
   * @returns O valor do 'timestamp' ou undefined se o formato não for válido.
   */
  static timestamp(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RTK=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'device address' de uma string que contém "RDA=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'device address'.
   * @returns O valor do 'device address' ou undefined se o formato não for válido.
   */
  static device_address(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RDA=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'device eui' de uma string que contém "RDE=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'device eui'.
   * @returns O valor do 'device eui' ou undefined se o formato não for válido.
   */
  static device_eui(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RDE=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'application eui' de uma string que contém "RAP=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'application eui'.
   * @returns O valor do 'application eui' ou undefined se o formato não for válido.
   */
  static application_eui(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RAP=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'application key' de uma string que contém "RAK=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'application key'.
   * @returns O valor do 'application key' ou undefined se o formato não for válido.
   */
  static application_key(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RAK=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'application session key' de uma string que contém "RASK=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'application session key'.
   * @returns O valor do 'application session key' ou undefined se o formato não for válido.
   */
  static application_session_key(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RASK=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do 'network session key' de uma string que contém "RASK=" seguido de uma chave.
   *
   * @param input - A string que contém a informação do 'network session key'.
   * @returns O valor do 'network session key' ou undefined se o formato não for válido.
   */
  static network_session_key(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RNK=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai o valor do firmware de uma string que contém "RFW=" seguido de um número.
   *
   * @param input - A string que contém a informação do firmware.
   * @returns O valor do firmware ou undefined se o formato não for válido.
   */
  static firmware(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFW=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  /**
   * Extrai um objeto da resposta do comando auto test.
   *
   * @param input - A string que contém a informação do autotest.
   * @returns O objeto do resultado do auto test ou undefined se o formato não for válido.
   */
  static auto_test(input: string): BwsLora.AutoTest | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('AUTOTEST=');
    if (parts.length < 2) return undefined;
    const splited = parts[1].split(',');
    return splited.reduce((acc, cur) => {
      const [key, value] = cur.split(':');
      acc[key as keyof BwsLora.AutoTest] = value;
      return acc;
    }, {} as BwsLora.AutoTest);
  }

  /**
   * Extrai o valor do odômetro de uma string que contém "RODM=" seguido de um número.
   *
   * @param input - A string que contém a informação do odômetro.
   * @returns O valor numérico do odômetro ou undefined se o formato não for válido.
   */
  static odometer(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RODM=');
    if (parts.length < 2) return undefined;

    const odometerValue = parts[1].trim();

    const value = parseFloat(odometerValue);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor do tempo de transmissão de ignição desligada de uma string que contém "RCW=" seguido de um número.
   *
   * @param input - A string que contém a informação do tempo de transmissão de ignição desligada.
   * @returns O valor numérico do tempo de transmissão de ignição desligada ou undefined se o formato não for válido.
   */
  static data_transmission_sleep(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RCW=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = parseFloat(data);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da tensão 12V de uma string que contém "RIG1=110,150"
   *
   * @param input - A string que contém a informação da tensão 12V.
   * @returns O valor numérico da tensão 12V ou undefined se o formato não for válido.
   */
  static virtual_ignition_12v(input: string): { initial: number; final: number } | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIG1=');
    if (parts.length < 2) return undefined;
    const [initial, final] = parts[1].split(',');
    if (!initial || !final) return undefined;
    return {
      initial: parseFloat(initial),
      final: parseFloat(final),
    };
  }

  /**
   * Extrai o valor da tensão 24V de uma string que contém "RIG2=276,281"
   *
   * @param input - A string que contém a informação da tensão 24V.
   * @returns O valor numérico da tensão 24V ou undefined se o formato não for válido.
   */
  static virtual_ignition_24v(input: string): { initial: number; final: number } | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIG2=');
    if (parts.length < 2) return undefined;
    const [initial, final] = parts[1].split(',');
    if (!initial || !final) return undefined;
    return {
      initial: parseFloat(initial),
      final: parseFloat(final),
    };
  }

  static heading(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFH=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    if (value !== '00' && value !== '01') return undefined;

    return value === '01';
  }

  static heading_event_mode(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFHV=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    if (value !== '00' && value !== '01') return undefined;

    return value === '01';
  }

  /**
   * Extrai o valor do ângulo de uma string que contém "RFA=30"
   *
   * @param input - A string que contém a informação do ângulo.
   * @returns O valor numérico do ângulo ou undefined se o formato não for válido.
   */
  static heading_detection_angle(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFA=');

    if (parts.length < 2) return undefined;

    const angle = parts[1].trim();

    const value = parseFloat(angle);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da velocidade de uma string que contém "RFV=100"
   *
   * @param input - A string que contém a informação da velocidade.
   * @returns O valor numérico da velocidade ou undefined se o formato não for válido.
   */
  static speed_alert_threshold(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFV=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da sensibilidade do acelerometro quando ligado de uma string que contém "RFTON=2"
   *
   * @param input - A string que contém a informação da sensibilidade do acelerometro quando ligado.
   * @returns O valor numérico da sensibilidade do acelerometro quando ligado ou undefined se o formato não for válido.
   */
  static accel_threshold_for_ignition_on(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFTON=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da sensibilidade do acelerometro quando desligado de uma string que contém "RFTOF=3"
   *
   * @param input - A string que contém a informação da sensibilidade do acelerometro quando desligado.
   * @returns O valor numérico da sensibilidade do acelerometro quando desligado ou undefined se o formato não for válido.
   */
  static accel_threshold_for_ignition_off(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFTOF=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da sensibilidade do acelerometro quando violado de uma string que contém "RFAV=150"
   *
   * @param input - A string que contém a informação da sensibilidade do acelerometro quando violado.
   * @returns O valor numérico da sensibilidade do acelerometro quando violado ou undefined se o formato não for válido.
   */
  static accel_threshold_for_movement(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFAV=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da aceleração máxima de uma string que contém "RFMA=50"
   *
   * @param input - A string que contém a informação da aceleração máxima.
   * @returns O valor numérico da aceleração máxima ou undefined se o formato não for válido.
   */
  static harsh_acceleration_threshold(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFMA=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  /**
   * Extrai o valor da desaceleração máxima de uma string que contém "RFMD=45"
   *
   * @param input - A string que contém a informação da desaceleração máxima.
   * @returns O valor numérico da desaceleração máxima ou undefined se o formato não for válido.
   */
  static harsh_braking_threshold(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RFMD=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  static data_transmission_position(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RWTR=');

    if (parts.length < 2) return undefined;

    const speed = parts[1].trim();

    const value = parseFloat(speed);

    return Number.isNaN(value) ? undefined : value;
  }

  static led_lighting(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RLED=');

    if (parts.length < 2) return undefined;

    const led = parts[1].trim();

    const value = led.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static p2p_mode_duration(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RLTO=');

    if (parts.length < 2) return undefined;

    const mode = parts[1].trim();

    const value = parseFloat(mode);

    return Number.isNaN(value) ? undefined : value;
  }

  static lorawan_mode_duration(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RWTO=');

    if (parts.length < 2) return undefined;

    const mode = parts[1].trim();

    const value = parseFloat(mode);

    return Number.isNaN(value) ? undefined : value;
  }

  static input_1(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN1=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static input_2(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN2=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static input_3(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN3=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static input_4(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN4=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static input_5(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN5=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static input_6(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RIN6=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static full_configuration_table(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RC=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static fifo_send_and_hold_times(input: string): number[] | undefined {
    if (typeof input !== 'string') return undefined;

    // Regex: captura dois números (inteiros ou decimais) após "RFIFO="
    const match = input.match(/^RFIFO=(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)$/);
    if (!match) return undefined;

    const [, sendStr, holdStr] = match;
    const send = parseFloat(sendStr);
    const hold = parseFloat(holdStr);

    // Caso queira garantir números válidos, embora a regex já filtre
    if (Number.isNaN(send) || Number.isNaN(hold)) return undefined;

    return [send, hold];
  }

  static lorawan_data_transmission_event(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('REWTR=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = parseFloat(data);

    return Number.isNaN(value) ? undefined : value;
  }

  static p2p_data_transmission_event(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RELTR=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = parseFloat(data);

    return Number.isNaN(value) ? undefined : value;
  }

  static data_transmission_status(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RTS=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = parseFloat(data);

    return Number.isNaN(value) ? undefined : value;
  }

  static full_functionality_table(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RF=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static activation_type(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RACT=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    if (value !== '00' && value !== '01') return undefined;

    return value;
  }

  static mcu_configuration(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;

    const parts = input.split('RMC=');

    if (parts.length < 2) return undefined;

    const data = parts[1].trim();

    const value = data.replace(/\s+/g, '');

    return value.length ? value : undefined;
  }

  static output_table(input: string): number[] | undefined {
    if (typeof input !== 'string') return undefined;

    // Regex: captura três números (inteiros ou decimais) após "ROUT="
    const match = input.match(/^ROUT=(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)$/);
    if (!match) return undefined;

    const [, firstStr, secondStr, thirdStr] = match;
    const first = parseFloat(firstStr);
    const second = parseFloat(secondStr);
    const third = parseFloat(thirdStr);

    // Caso queira garantir números válidos, embora a regex já filtre
    if (Number.isNaN(first) || Number.isNaN(second) || Number.isNaN(third)) return undefined;

    return [first, second, third];
  }
}
